/**
 * Query params Stripe appends to `return_url` when `confirmSetup` had to send
 * the customer off-site (3DS challenge, redirect-based payment method).
 */
const REDIRECT_QUERY_PARAMS = [
  "setup_intent",
  "setup_intent_client_secret",
  "redirect_status",
] as const;

const CLIENT_SECRET_PARAM = "setup_intent_client_secret";

let clientSecretFromUrl: string | null | undefined;
let consumed = false;

function readClientSecret() {
  if (clientSecretFromUrl === undefined) {
    clientSecretFromUrl = new URLSearchParams(window.location.search).get(
      CLIENT_SECRET_PARAM,
    );
  }
  return clientSecretFromUrl;
}

/**
 * Whether this page load is a return from an off-site setup. Callable from
 * anywhere in the tree and at any time: the URL is read once and cached, so
 * the answer stays stable after `consumeSetupIntentRedirect` cleans the URL.
 */
export function peekSetupIntentRedirect(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean(readClientSecret());
}

/**
 * Takes the client secret of the returning setup and drops Stripe's params
 * from the URL — the secret should not linger in the address bar or in
 * history, and a reload must not replay the confirmation. Returns `null` once
 * the secret has been taken, so the result is only ever handled once.
 */
export function consumeSetupIntentRedirect() {
  if (typeof window === "undefined" || consumed) return null;

  const clientSecret = readClientSecret();
  consumed = true;
  if (!clientSecret) return null;

  const url = new URL(window.location.href);
  REDIRECT_QUERY_PARAMS.forEach((param) => url.searchParams.delete(param));
  window.history.replaceState(
    null,
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );

  return clientSecret;
}
